import {
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuRoot,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@/components/ui/shadcn/ContextMenu/ContextMenuPrimitives";
import { copyToClipboard } from "@/lib/utils/copyToClipboard";
import { Copy, Pencil, Trash2, Unlink } from "lucide-react";
import { useState, type MouseEvent, type ReactNode } from "react";

export interface ContextMenuAction {
  disabled?: boolean;
  label?: string;
  onSelect: () => void;
}

export interface ContextMenuCopyAction {
  disabled?: boolean;
  label?: string;
  successLabel?: string;
  value: string;
}

type OptionalAction<T> = T | false;

interface ContextMenuProps {
  disabled?: boolean;
  children?: ReactNode;
  contentClassName?: string;
  copy?: OptionalAction<ContextMenuCopyAction>;
  delete?: OptionalAction<ContextMenuAction>;
  entityName: string;
  onContextMenu?: (event: MouseEvent<HTMLElement>) => void;
  onOpenChange?: (open: boolean) => void;
  open?: OptionalAction<ContextMenuAction>;
  trigger: ReactNode;
  unlink?: OptionalAction<ContextMenuAction>;
}

export const ContextMenu = ({
  disabled,
  children,
  contentClassName,
  copy,
  delete: deleteAction,
  entityName,
  onContextMenu,
  onOpenChange,
  open,
  trigger,
  unlink,
}: ContextMenuProps) => {
  const [positionKey, setPositionKey] = useState(0);
  const hasStandardActions = Boolean(open || copy);
  const hasCustomActions = Boolean(children);

  const handleContextMenu = (event: MouseEvent<HTMLElement>) => {
    setPositionKey((currentPositionKey) => currentPositionKey + 1);
    onContextMenu?.(event);
  };

  return (
    <ContextMenuRoot onOpenChange={onOpenChange}>
      <ContextMenuTrigger
        asChild
        disabled={disabled}
        className="group data-[state=open]:bg-muted/50"
        onContextMenu={disabled ? undefined : handleContextMenu}
      >
        {trigger}
      </ContextMenuTrigger>
      <ContextMenuContent key={positionKey} className={contentClassName}>
        {open ? (
          <ContextMenuItem
            disabled={open.disabled}
            onSelect={() => {
              open.onSelect();
            }}
          >
            <Pencil />
            {open.label ?? `Edit ${entityName}`}
          </ContextMenuItem>
        ) : null}
        {copy ? (
          <ContextMenuItem
            disabled={copy.disabled}
            onSelect={() => {
              void copyToClipboard(copy.value, copy.successLabel ?? entityName);
            }}
          >
            <Copy />
            {copy.label ?? `Copy ${entityName}`}
          </ContextMenuItem>
        ) : null}
        {hasCustomActions ? (
          <>
            {hasStandardActions ? <ContextMenuSeparator /> : null}
            {children}
          </>
        ) : null}
        {unlink || deleteAction ? (
          <>
            {hasStandardActions || hasCustomActions ? (
              <ContextMenuSeparator />
            ) : null}
            {unlink ? (
              <ContextMenuItem
                disabled={unlink.disabled}
                onSelect={() => {
                  unlink.onSelect();
                }}
              >
                <Unlink />
                {unlink.label ?? `Unlink ${entityName}`}
              </ContextMenuItem>
            ) : null}
            {deleteAction ? (
              <ContextMenuItem
                disabled={deleteAction.disabled}
                onSelect={() => {
                  deleteAction.onSelect();
                }}
              >
                <Trash2 />
                {deleteAction.label ?? `Delete ${entityName}`}
              </ContextMenuItem>
            ) : null}
          </>
        ) : null}
      </ContextMenuContent>
    </ContextMenuRoot>
  );
};
